// Temporary page: every non-admin Button variant, rendered on a mid-tone blue
// that keeps the white, dark, red and outline variants all readable, with the
// number of <Button> instances using it and its rendered height. Counts come
// from usage.json, regenerated by: node scripts/count-button-variants.mjs
import React, { useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import defaultVariants from "aldoo-ra/Button/variants"
import projectVariants from "../Variants/Button"
import usage from "./usage.json"

const isAdmin = (name) => name.startsWith("admin")

// Group = the name without the sf_ prefix, up to the first "_" (primary_l,
// primary, primary_s → primary). primary and secondary lead, shop then
// icon come last, the other groups follow alphabetically in between. Inside a group: the plain variants first,
// then the other subgroups alphabetically, red (destructive) ones last; each
// subgroup large to small.
const LEADING_GROUPS = ["primary", "secondary"]
const TRAILING_GROUPS = ["shop", "icon"]
const groupRank = (group) => {
  const i = LEADING_GROUPS.indexOf(group)
  if (i !== -1) return i
  const j = TRAILING_GROUPS.indexOf(group)
  return j === -1 ? LEADING_GROUPS.length : LEADING_GROUPS.length + 1 + j
}
const SIZE_RANK = { l: 0, "": 1, m: 2, sm: 3, s: 3, xs: 4 }
const sortKey = (name) => {
  const [group, ...rest] = name.replace(/^sf_/, "").split("_")
  // a trailing size token splits the suffix into subgroup + size
  // (white_l → white + l), so each subgroup lists large to small too
  const size = rest.length && rest[rest.length - 1] in SIZE_RANK ? rest.pop() : ""
  const suffix = rest.join("_")
  const subRank = suffix === "" ? 0 : suffix.startsWith("red") ? 2 : 1
  return { group, subRank, suffix, rank: SIZE_RANK[size] }
}
const byGroup = (a, b) => {
  const ka = sortKey(a.name)
  const kb = sortKey(b.name)
  return (
    groupRank(ka.group) - groupRank(kb.group) ||
    ka.group.localeCompare(kb.group) ||
    ka.subRank - kb.subRank ||
    ka.suffix.localeCompare(kb.suffix) ||
    ka.rank - kb.rank
  )
}

const entries = [
  ...Object.keys(projectVariants).map((name) => ({ name, source: "project" })),
  ...Object.keys(defaultVariants)
    .filter((name) => !(name in projectVariants))
    .map((name) => ({ name, source: "aldoo-ra default" })),
]
  .filter(({ name }) => !isAdmin(name))
  // an aldoo-ra default that no file of this project uses is not ours to show
  .filter(({ name, source }) => source === "project" || usage.projectCounts?.[name])
  .sort(byGroup)

const defined = new Set([...Object.keys(defaultVariants), ...Object.keys(projectVariants)])
const undefinedInUse = Object.keys(usage.counts).filter((name) => !defined.has(name) && !isAdmin(name))

// The height is measured from the rendered button, so variants without an
// explicit h-[…] class report their real height too.
function VariantCard({ name, source, count }) {
  const slot = useRef(null)
  const [height, setHeight] = useState(null)

  useEffect(() => {
    const button = slot.current?.firstElementChild
    if (button) setHeight(Math.round(button.getBoundingClientRect().height))
  }, [])

  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-white/40 bg-white/10 p-6">
      <div ref={slot} className="flex min-h-[64px] items-center justify-center">
        <Button variant={name}>{name === "sf_shop" ? <><span>Shop now</span><span>→</span></> : "Button"}</Button>
      </div>
      <div className="text-center">
        <div className="font-mono text-[14px] font-bold text-text">{name}</div>
        <div className="text-[13px] text-text">
          {count} {count === 1 ? "instance" : "instances"}
        </div>
        <div className="font-mono text-[13px] text-text">h-[{height === null ? "…" : `${height}px`}]</div>
        <div className="text-[13px] text-text">{source}</div>
      </div>
    </div>
  )
}

export default function ButtonShowcasePage() {
  return (
    <div className="min-h-screen bg-[#86b0d6] px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <h1 className="text-[28px] font-bold text-text">Button variants</h1>
        <p className="mt-1 text-[15px] text-text">
          {entries.length} variants (admin variants and unused aldoo-ra defaults excluded). Counts generated {usage.generatedAt.slice(0, 10)}.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map(({ name, source }) => (
            <VariantCard key={name} name={name} source={source} count={usage.counts[name] || 0} />
          ))}
        </div>

        {undefinedInUse.length > 0 && (
          <p className="mt-8 text-[14px] text-text">
            Used in code but not defined (render unstyled):{" "}
            {undefinedInUse.map((name) => `${name} (${usage.counts[name]})`).join(", ")}
          </p>
        )}
        {usage.unresolved.length > 0 && (
          <p className="mt-2 text-[14px] text-text">
            Variant set from a prop, not counted: {usage.unresolved.join(", ")}
          </p>
        )}
      </div>
    </div>
  )
}
